/**
 * db:// query helpers — client-side twins of the proxy guardrails so UI/BFF can
 * pre-validate a datapoint before it reaches `POST /ontology/tags/batch`. The SELECT-only
 * gate mirrors proxy-server's `internal/ontology/dbguard/selectonly.go`; the
 * transport/sync-mode normalizers mirror `internal/ontology/tag/db_path.go`; and
 * `validateDbDatapoint` mirrors `validateDBBlock` (`tag/router.go`). Keep them in
 * lockstep — a query the proxy would reject must not pass here, and vice versa.
 */

import { DB_SYNC_MODE, DB_TRANSPORT, type DbDatapointInput } from './wire-types'

// `into` (and MySQL `outfile`/`dumpfile`) are blocked because `SELECT … INTO`
// is a write (PostgreSQL/SQL Server create a table, MySQL writes a file) that
// still starts with SELECT, so a single-statement SELECT is not proof of
// read-only. Executable `into` only appears in those write forms; quoted
// identifiers/literals are masked by sqlCode before this runs.
// SQL Server batch verbs (BACKUP, SET, BEGIN, …) and file/process functions
// (pg_read_file, LOAD_FILE, read_csv, set_config, dblink, …). Keep in lockstep
// with guard.dbStatementAlt and guard.dbFunctionAlt.
const DB_STATEMENT_ALTERNATION =
  'insert|update|delete|merge|drop|alter|truncate|create|grant|revoke|into|outfile|dumpfile|exec|execute|backup|restore|shutdown|dbcc|reconfigure|waitfor|checkpoint|deny|kill|disable|enable|use|begin|commit|rollback|set|updatetext|writetext|readtext|setuser|revert|updlock|xlock|tablockx|holdlock|tablock'
const DB_FUNCTION_ALTERNATION =
  'openrowset|opendatasource|openquery|xp_cmdshell|pg_read_file|pg_read_binary_file|pg_ls_dir|pg_ls_logdir|pg_ls_waldir|pg_ls_archive_statusdir|pg_ls_tmpdir|pg_ls_logicalsnapdir|pg_ls_logicalmapdir|pg_ls_replslotdir|pg_stat_file|pg_terminate_backend|pg_cancel_backend|pg_sleep(?:_[a-z0-9_]+)?|pg_reload_conf|pg_rotate_logfile|pg_switch_wal|pg_promote|pg_wal_replay_pause|pg_wal_replay_resume|pg_logical_emit_message|pg_drop_replication_slot|pg_logical_slot_get_binary_changes|pg_logical_slot_get_changes|pg_file_write|pg_file_unlink|pg_file_rename|pg_file_sync|pg_logdir_ls|lo_import|lo_export|lo_put|lo_unlink|lo_from_bytea|lo_create|lo_get|lo_write|lo_open|lo_creat|lo_truncate64|dblink(?:_[a-z0-9_]+)?|set_config|setval|nextval|pg_(?:try_)?advisory_(?:xact_)?lock(?:_shared)?|get_lock|load_file|read_csv|read_csv_auto|read_blob|read_text|read_json|read_json_auto|read_ndjson|read_ndjson_auto|read_parquet|sniff_csv|parquet_scan|sqlite_scan|postgres_scan|mysql_scan|iceberg_scan|delta_scan|pg_stat_reset(?:_[a-z0-9_]+)?|pg_replication_origin_[a-z0-9_]+|pg_replication_slot_advance|pg_create_logical_replication_slot|pg_create_physical_replication_slot|pg_copy_logical_replication_slot|pg_copy_physical_replication_slot|pg_logical_slot_peek_binary_changes|pg_logical_slot_peek_changes|pg_notify|lo_truncate|pg_log_backend_memory_contexts|pg_create_restore_point|pg_backup_start|pg_backup_stop|pg_start_backup|pg_stop_backup|pg_switch_xlog|pg_xlog_replay_pause|pg_xlog_replay_resume|pg_logfile_rotate|pg_file_read|pg_stat_statements_reset|fn_get_audit_file|fn_trace_gettable|fn_xe_file_target_read_file|fn_dump_dblog|fn_dblog|fn_full_dblog|dm_os_enumerate_filesystem|dm_os_file_exists|xp_dirtree|xp_fileexist|xp_subdirs|xp_regread|xp_regwrite|xp_fixeddrives|postgres_query|postgres_execute|mysql_query|mysql_execute|sqlite_attach|parquet_metadata|parquet_schema|parquet_file_metadata|parquet_kv_metadata|master_pos_wait|source_pos_wait|release_lock|release_all_locks'
const DB_STATEMENT_PHRASE =
  'open\\s+symmetric\\b|open\\s+master\\s+key\\b|close\\s+all\\s+symmetric\\b|close\\s+symmetric\\b|close\\s+master\\s+key\\b|send\\s+on\\s+conversation\\b|end\\s+conversation\\b|move\\s+conversation\\b|get\\s+conversation\\b|add\\s+counter\\s+signature\\b|add\\s+signature\\b|add\\s+sensitivity\\b|save\\s+(?:transaction|tran)\\b|receive\\s+(?:top\\b|\\*|\\[?\\s*(?:conversation_|message_|service_|validation\\b|status\\b|priority\\b|queuing_order\\b))|sleep\\s*\\(|benchmark\\s*\\('
const DB_FUNCTION_KEYWORDS = new RegExp(`\\b(${DB_FUNCTION_ALTERNATION})\\b`, 'i')
const DB_WRITE_KEYWORDS = new RegExp(
  `\\b(${DB_STATEMENT_ALTERNATION}|${DB_FUNCTION_ALTERNATION})\\b`,
  'i'
)
const DB_STATEMENT_PHRASES = new RegExp(`\\b(?:${DB_STATEMENT_PHRASE})`, 'i')
const DB_UNICODE_ESCAPE_CLAUSE = /\buescape\b/i
// Line (`-- …`) and block (`/* … */`) comments; block matches across newlines.
const DB_COMMENT_STRIPPER = /--[^\n]*|\/\*[\s\S]*?\*\//g
const WRITE_DML_START =
  /^\s*(with\b[\s\S]+?\b(insert|update|delete|merge)\b|(insert|update|delete|merge)\b)/is

function isSqlIdentifierPart(char: string | undefined): boolean {
  if (!char) return false
  const code = char.charCodeAt(0)
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    char === '_' ||
    char === '$' ||
    code >= 0x80
  )
}

/**
 * Masks comments, string literals, and quoted identifiers while preserving
 * executable SQL tokens. Returns null for an unterminated quoted construct.
 * This mirrors proxy dbguard.sqlCode.
 */
function sqlCode(q: string): string | null {
  const code = q.split('')
  const mask = (start: number, end: number) => {
    for (let j = start; j < end; j++) {
      if (code[j] !== '\n' && code[j] !== '\r') code[j] = ' '
    }
  }

  for (let i = 0; i < q.length; ) {
    if (q.startsWith('--', i)) {
      // MySQL only comments `--` when whitespace follows. See isLineComment
      // in the Go guard.
      const next = q[i + 2]
      if (next !== undefined && !/[\t\n\v\f\r ]/.test(next)) {
        i++
        continue
      }
      const start = i
      i += 2
      while (i < q.length && q[i] !== '\n' && q[i] !== '\r') i++
      mask(start, i)
      continue
    }
    if (q.startsWith('/*', i)) {
      // MySQL executes `/*! ... */`. Masking it would hide the statement.
      if (q.startsWith('/*!', i)) return null
      // First `*/` ends the comment. MySQL and SQL Server do not nest.
      const start = i
      i += 2
      const close = q.indexOf('*/', i)
      if (close < 0) return null
      i = close + 2
      mask(start, i)
      continue
    }

    const quote = q[i]
    if (quote === "'" || quote === '"' || quote === '`') {
      const start = i
      const backslashEscapes =
        quote === "'" &&
        (q[start - 1] === 'e' || q[start - 1] === 'E') &&
        (start === 1 || !/[A-Za-z0-9_]/.test(q[start - 2]))
      i++
      let closed = false
      while (i < q.length) {
        if (backslashEscapes && q[i] === '\\' && i + 1 < q.length) {
          i += 2
        } else if (q[i] === '\\' && q[i + 1] === quote) {
          // `\'` / `\"` / `` \` `` disagrees between MySQL and PostgreSQL.
          return null
        } else if (q[i] !== quote) {
          i++
        } else if (q[i + 1] === quote) {
          i += 2
        } else {
          i++
          closed = true
          break
        }
      }
      if (!closed) return null
      // Quotes are masked below, so a later keyword scan cannot see
      // "pg_read_file". That spelling is still a function call.
      if (quote !== "'" && quotedIdentifierBlocked(q, start, i, quote)) return null
      mask(start, i)
      continue
    }

    // SQL Server `[$$]` is a legal identifier. `$` is not an identifier
    // character, so the dollar-quote rule would hide the next statement.
    //
    // Known issue: PostgreSQL and DuckDB use `[` for arrays, and a string
    // inside an array can contain `]`. This scan stops at the first `]`, so
    // `SELECT (ARRAY[1,2])[length(']')]; DROP TABLE t; SELECT length(']')`
    // is accepted. Direct Postgres reads still run in a read-only
    // transaction. WebDev, DuckDB, SQL Server, and tag registration do not.
    // `SELECT arr[1]` and `SELECT [into]` stay legal.
    if (q[i] === '[') {
      const start = i
      i++
      let closed = false
      while (i < q.length) {
        if (q[i] !== ']') {
          i++
          continue
        }
        if (q[i + 1] === ']') {
          i += 2
          continue
        }
        i++
        closed = true
        break
      }
      if (!closed) return null
      if (quotedIdentifierBlocked(q, start, i, ']') || receiveBracketColumn(q, start, i))
        return null
      mask(start, i)
      continue
    }

    if (q[i] === '$' && !isSqlIdentifierPart(q[i - 1])) {
      const delimiter = q.slice(i).match(/^\$(?:[A-Za-z_][A-Za-z0-9_]*)?\$/)?.[0]
      if (delimiter) {
        const start = i
        i += delimiter.length
        const end = q.indexOf(delimiter, i)
        if (end < 0) return null
        i = end + delimiter.length
        mask(start, i)
        continue
      }
    }

    i++
  }

  return code.join('')
}

const RECEIVE_BRACKET_COL =
  /^(?:conversation_|message_|service_|validation|status|priority|queuing_order)/i

// Masking brackets hides a RECEIVE column list from the phrase check.
// SELECT [conversation_handle] is still a normal column.
function receiveBracketColumn(q: string, start: number, end: number): boolean {
  if (!previousWordIs(q, start, 'receive')) return false
  const ident = q
    .slice(start + 1, end - 1)
    .split(']]')
    .join(']')
  return RECEIVE_BRACKET_COL.test(ident)
}

function previousWordIs(q: string, i: number, word: string): boolean {
  let j = i - 1
  while (j >= 0 && /[\t\n\v\f\r ]/.test(q[j])) j--
  if (j < 0) return false
  const end = j + 1
  while (j >= 0 && isSqlIdentifierPart(q[j])) j--
  return q.slice(j + 1, end).toLowerCase() === word
}

function quotedIdentifierBlocked(q: string, start: number, end: number, quote: string): boolean {
  const raw = q.slice(start + 1, end - 1)
  let ident = raw.split(quote + quote).join(quote)
  if (quote === '"' && hasUnicodeIdentPrefix(q, start)) {
    if (DB_UNICODE_ESCAPE_CLAUSE.test(q)) return true
    const decoded = decodeUnicodeIdent(ident)
    if (decoded === null) return true
    ident = decoded
  }
  const call = looksLikeCall(q, end)
  if (call === null) return true
  if (DB_FUNCTION_KEYWORDS.test(ident)) return true
  return call && DB_WRITE_KEYWORDS.test(ident)
}

function hasUnicodeIdentPrefix(q: string, quoteAt: number): boolean {
  if (quoteAt < 2 || q[quoteAt - 1] !== '&') return false
  const u = q[quoteAt - 2]
  if (u !== 'U' && u !== 'u') return false
  return quoteAt === 2 || !isSqlIdentifierPart(q[quoteAt - 3])
}

function decodeUnicodeIdent(raw: string): string | null {
  let out = ''
  for (let i = 0; i < raw.length; ) {
    if (raw[i] !== '\\') {
      out += raw[i]
      i++
      continue
    }
    if (i + 1 >= raw.length) return null
    if (raw[i + 1] === '\\') {
      out += '\\'
      i += 2
      continue
    }
    if (raw[i + 1] === '+') {
      const hex = raw.slice(i + 2, i + 8)
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return null
      const codePoint = Number.parseInt(hex, 16)
      if (codePoint > 0x10ffff) return null
      out += String.fromCodePoint(codePoint)
      i += 8
      continue
    }
    const hex = raw.slice(i + 1, i + 5)
    if (!/^[0-9A-Fa-f]{4}$/.test(hex)) return null
    out += String.fromCodePoint(Number.parseInt(hex, 16))
    i += 5
  }
  return out
}

/** `true` when the next token is `(`, `null` when the lookahead is not trustworthy. */
function looksLikeCall(q: string, i: number): boolean | null {
  while (i < q.length) {
    const ch = q[i]
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\v' || ch === '\f') {
      i++
      continue
    }
    if (q.startsWith('--', i)) {
      const next = q[i + 2]
      if (next !== undefined && !/[\t\n\v\f\r ]/.test(next)) return q[i] === '('
      i += 2
      while (i < q.length && q[i] !== '\n' && q[i] !== '\r') i++
      continue
    }
    if (q.startsWith('/*', i)) {
      if (q.startsWith('/*!', i)) return null
      const close = q.indexOf('*/', i + 2)
      if (close < 0) return null
      i = close + 2
      continue
    }
    return ch === '('
  }
  return false
}

type RelationScan = { pending: boolean; inList: boolean; fromDepth: number }

/**
 * DuckDB reads a string in a FROM or JOIN list as a server file, including
 * after a comma (`FROM t, '/etc/passwd'`). PostgreSQL uses FROM before a
 * string only inside substring, extract, overlay, and trim.
 */
function hasFileRelation(q: string): boolean {
  const fromSyntax: boolean[] = []
  const inFromSyntax = () => fromSyntax.length > 0 && fromSyntax[fromSyntax.length - 1]
  const saved: RelationScan[] = []
  let cur: RelationScan = { pending: false, inList: false, fromDepth: 0 }
  let depth = 0
  let lastIdent = ''
  const fromListEnd =
    /^(where|group|order|having|limit|offset|fetch|window|union|except|intersect|qualify|for)$/i
  for (let i = 0; i < q.length; ) {
    const ch = q[i]
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\v' || ch === '\f') {
      i++
      continue
    }
    if (q.startsWith('--', i)) {
      const next = q[i + 2]
      if (next !== undefined && !/[\t\n\v\f\r ]/.test(next)) {
        cur = { ...cur, pending: false }
        lastIdent = ''
        i++
        continue
      }
      i += 2
      while (i < q.length && q[i] !== '\n' && q[i] !== '\r') i++
      continue
    }
    if (q.startsWith('/*', i)) {
      if (q.startsWith('/*!', i)) return true
      const close = q.indexOf('*/', i + 2)
      if (close < 0) return true
      i = close + 2
      continue
    }
    const dollar = !isSqlIdentifierPart(q[i - 1])
      ? q.slice(i).match(/^\$(?:[A-Za-z_][A-Za-z0-9_]*)?\$/)?.[0]
      : undefined
    if (ch === "'" || dollar) {
      if (cur.pending) return true
      const end = skipLiteral(q, i)
      if (end === null) return true
      i = end
      cur = { ...cur, pending: false }
      lastIdent = ''
      continue
    }
    if (ch === '"' || ch === '`') {
      const end = skipLiteral(q, i)
      if (end === null) return true
      i = end
      cur = { ...cur, pending: false }
      lastIdent = ''
      continue
    }
    if (ch === '(') {
      // DuckDB reads a parenthesized string here as a file. A subquery starts with a word.
      if (cur.pending && nextIsFileLiteral(q, i + 1)) return true
      const name = lastIdent.toLowerCase()
      fromSyntax.push(
        name === 'substring' || name === 'extract' || name === 'overlay' || name === 'trim'
      )
      saved.push({ pending: false, inList: cur.inList, fromDepth: cur.fromDepth })
      cur = { pending: false, inList: false, fromDepth: 0 }
      lastIdent = ''
      depth++
      i++
      continue
    }
    if (ch === ')') {
      fromSyntax.pop()
      cur = saved.pop() ?? { pending: false, inList: false, fromDepth: 0 }
      if (depth > 0) depth--
      lastIdent = ''
      i++
      continue
    }
    if (ch === ',') {
      cur = {
        ...cur,
        pending: cur.inList && depth === cur.fromDepth
      }
      lastIdent = ''
      i++
      continue
    }
    if (isIdentStart(ch)) {
      const start = i
      i++
      while (i < q.length && isSqlIdentifierPart(q[i])) i++
      const word = q.slice(start, i)
      // IS [NOT] DISTINCT FROM 'literal' is a comparison, not a table list.
      const prevWord = lastIdent
      lastIdent = word
      if (cur.pending && /^[eEnNxX]$/.test(word) && nextIsStringLiteral(q, i)) return true
      if (!inFromSyntax() && !/^distinct$/i.test(prevWord) && /^(from|join)$/i.test(word)) {
        cur = { pending: true, inList: true, fromDepth: depth }
      } else if (!cur.pending && cur.inList && depth === cur.fromDepth && fromListEnd.test(word)) {
        cur = { ...cur, inList: false, pending: false }
      } else {
        cur = { ...cur, pending: false }
      }
      continue
    }
    lastIdent = ''
    cur = { ...cur, pending: false }
    i++
  }
  return false
}

function isIdentStart(char: string): boolean {
  if (!char) return false
  const code = char.charCodeAt(0)
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122) || char === '_' || code >= 0x80
}

function nextIsFileLiteral(q: string, i: number): boolean {
  while (i < q.length) {
    const ch = q[i]
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\v' || ch === '\f') {
      i++
      continue
    }
    if (ch === "'") return true
    return ch === '$' && /^\$(?:[A-Za-z_][A-Za-z0-9_]*)?\$/.test(q.slice(i))
  }
  return false
}

function nextIsStringLiteral(q: string, i: number): boolean {
  while (i < q.length) {
    const ch = q[i]
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\v' || ch === '\f') {
      i++
      continue
    }
    return ch === "'"
  }
  return false
}

function skipLiteral(q: string, i: number): number | null {
  const dollar = !isSqlIdentifierPart(q[i - 1])
    ? q.slice(i).match(/^\$(?:[A-Za-z_][A-Za-z0-9_]*)?\$/)?.[0]
    : undefined
  if (dollar) {
    const end = q.indexOf(dollar, i + dollar.length)
    if (end < 0) return null
    return end + dollar.length
  }
  const quote = q[i]
  const backslashEscapes =
    quote === "'" &&
    (q[i - 1] === 'e' || q[i - 1] === 'E') &&
    (i === 1 || !/[A-Za-z0-9_]/.test(q[i - 2] ?? ''))
  i++
  while (i < q.length) {
    if (backslashEscapes && q[i] === '\\' && i + 1 < q.length) {
      i += 2
      continue
    }
    if (q[i] === '\\' && q[i + 1] === quote) return null
    if (q[i] !== quote) {
      i++
      continue
    }
    if (q[i + 1] === quote) {
      i += 2
      continue
    }
    return i + 1
  }
  return null
}

/**
 * Reports whether `q` is a single read-only SELECT (or `WITH … SELECT`)
 * statement. Comments and quoted content are ignored, a single trailing
 * semicolon is tolerated, and any embedded second statement or executable
 * write keyword rejects.
 */
export function isSelectOnlyQuery(q: string): boolean {
  const code = sqlCode(q)
  if (code === null || hasFileRelation(q)) return false
  let body = code.trim()
  if (body.endsWith(';')) body = body.slice(0, -1).trim()
  if (body === '') return false
  if (body.includes(';')) return false
  if (!/^(select|with)\b/i.test(body)) return false
  return !DB_WRITE_KEYWORDS.test(body) && !DB_STATEMENT_PHRASES.test(body)
}

/**
 * Ensures `q` is a single parameterized write statement — not SELECT-only,
 * not multi-statement. Twin of proxy `writeback.ValidateWriteQuery`.
 * Returns a human-readable problem, or `null` when valid.
 */
export function validateWriteQuery(q: string): string | null {
  let body = q.replace(DB_COMMENT_STRIPPER, ' ').trim()
  if (body === '') {
    return 'parametrized_query is required'
  }
  body = body.replace(/[;\s]+$/, '')
  if (body.includes(';')) {
    return 'parametrized_query must be a single statement'
  }
  if (isSelectOnlyQuery(q)) {
    return 'parametrized_query must be a write statement (INSERT/UPDATE/DELETE/MERGE), not SELECT-only'
  }
  if (!WRITE_DML_START.test(body)) {
    return 'parametrized_query must start with INSERT, UPDATE, DELETE, MERGE, or a WITH … write'
  }
  return null
}

/**
 * Lowercases/trims a transport string, mapping the empty string to the
 * `webdev` default. Unknown values pass through so callers can reject them.
 */
export function normalizeDbTransport(s?: string): string {
  const t = (s ?? '').trim().toLowerCase()
  return t === '' ? DB_TRANSPORT.webdev : t
}

/**
 * Lowercases/trims a sync-mode string, mapping the empty string to the
 * `sample` default. Unknown values pass through so callers can reject them.
 */
export function normalizeDbSyncMode(s?: string): string {
  const m = (s ?? '').trim().toLowerCase()
  return m === '' ? DB_SYNC_MODE.sample : m
}

/**
 * Validates a db datapoint block. Returns a human-readable problem, or `null`
 * when valid. Mirrors the proxy `validateDBBlock` so the two agree on which
 * blocks are accepted. Callers may prefix the returned message with a field
 * path (e.g. `tags[0].db.`).
 */
export function validateDbDatapoint(db: DbDatapointInput): string | null {
  if ((db.query ?? '').trim() === '') {
    return 'query is required'
  }
  if (!isSelectOnlyQuery(db.query)) {
    return 'query must be a single read-only SELECT statement'
  }

  const transport = normalizeDbTransport(db.transport)
  if (transport !== DB_TRANSPORT.webdev && transport !== DB_TRANSPORT.direct) {
    return `transport must be "${DB_TRANSPORT.webdev}" or "${DB_TRANSPORT.direct}"`
  }

  const syncMode = normalizeDbSyncMode(db.sync_mode)
  if (syncMode !== DB_SYNC_MODE.sample && syncMode !== DB_SYNC_MODE.incremental) {
    return `sync_mode must be "${DB_SYNC_MODE.sample}" or "${DB_SYNC_MODE.incremental}"`
  }

  // value_column is optional: an empty value_column selects DataSet mode.
  // Incremental sync still needs a scalar value_column and a time_column
  // watermark — a watermarked sync has no meaning for a whole-table snapshot.
  if (syncMode === DB_SYNC_MODE.incremental) {
    if ((db.value_column ?? '').trim() === '') {
      return `value_column is required for sync_mode "${DB_SYNC_MODE.incremental}"`
    }
    if ((db.time_column ?? '').trim() === '') {
      return `time_column is required for sync_mode "${DB_SYNC_MODE.incremental}"`
    }
  }

  if (db.params !== undefined && !Array.isArray(db.params)) {
    return 'params must be a JSON array'
  }

  return null
}
